import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { QUERY_KEYS } from "../../querykeys";
import {
  getTodayStatus,
  checkIn,
  checkOut,
  getMonthlySummary,
  getCalendarHovers,
  submitMissingCheckOutRequest,
  MissingCheckOutPayload,
  getRegularizationStatus,
  getRegularizationRequestByUuid,
  actionRegularizationRequest,
  RegularizationActionPayload,
} from "../api/attendance";
import { ApiError } from "../../../types/apiError";
import { HolidayWorkCheckInPayload } from "../../../types/attendance";

// A Holiday Work check-in with a missing, false, or stale acknowledgement.
export const HOLIDAY_WORK_ACKNOWLEDGEMENT_REQUIRED = 'HOLIDAY_WORK_ACKNOWLEDGEMENT_REQUIRED';

export const useTodayStatusQuery = () => {
  return useQuery({
    queryKey: QUERY_KEYS.ATTENDANCE_TODAY_STATUS,
    queryFn: getTodayStatus,
  });
};

export const useCheckInMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // `| void` keeps the plain mutate() call (no body) valid for ordinary days.
    mutationFn: (body: HolidayWorkCheckInPayload | void) => checkIn(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ATTENDANCE_TODAY_STATUS });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_CALENDAR_HOVERS'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_MONTHLY_SUMMARY'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_DAY_SUMMARY'] });
      queryClient.invalidateQueries({ queryKey: ['EMPLOYEES_ATTENDANCE'] });
      toast.success("Checked in successfully!");
    },
    onError: (error: ApiError) => {
      toast.error(error.message || "Failed to check in");
    },
  });
};

export const useCheckOutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: checkOut,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ATTENDANCE_TODAY_STATUS });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_CALENDAR_HOVERS'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_MONTHLY_SUMMARY'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_DAY_SUMMARY'] });
      queryClient.invalidateQueries({ queryKey: ['EMPLOYEES_ATTENDANCE'] });
      toast.success("Checked out successfully!");
    },
    onError: (error: ApiError) => {
      toast.error(error.message || "Failed to check out");
    },
  });
};

export const useMonthlySummaryQuery = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.ATTENDANCE_MONTHLY_SUMMARY(startDate, endDate),
    queryFn: () => getMonthlySummary(startDate!, endDate!),
    enabled: Boolean(startDate && endDate),
  });
};

export const useCalendarHoversQuery = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.ATTENDANCE_CALENDAR_HOVERS(startDate, endDate),
    queryFn: () => getCalendarHovers(startDate!, endDate!),
    enabled: Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
};


export const useSubmitMissingCheckOutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attendanceRecordUuid,
      payload,
    }: {
      attendanceRecordUuid: string;
      payload: MissingCheckOutPayload;
    }) => submitMissingCheckOutRequest(attendanceRecordUuid, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ATTENDANCE_TODAY_STATUS });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_CALENDAR_HOVERS'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_MONTHLY_SUMMARY'] });
      queryClient.invalidateQueries({ queryKey: ['ATTENDANCE_DAY_SUMMARY'] 
      });
      if (res?.submissionMode === 'NOTIFICATION_ONLY') {
        toast.success("Check-out corrected directly!");
      } else {
        toast.success("Missing check-out request submitted successfully!");
      }
    },
    onError: (error: ApiError) => {
      console.error("Error submitting missing check-out request:", error);
      toast.error(error.message || "Failed to submit missing check-out request");
    },
  });
};

export const useRegularizationStatusQuery = (attendanceRecordUuid: string) => {
  return useQuery({
    queryKey: ['REGULARIZATION_STATUS', attendanceRecordUuid],
    queryFn: () => getRegularizationStatus(attendanceRecordUuid),
    enabled: !!attendanceRecordUuid,
  });
};

export const useRegularizationRequestQuery = (requestUuid?: string) => {
  return useQuery({
    queryKey: ['REGULARIZATION_REQUEST', requestUuid],
    queryFn: () => getRegularizationRequestByUuid(requestUuid!),
    enabled: Boolean(requestUuid),
  });
};

export const useActionRegularizationRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestUuid,
      payload,
    }: {
      requestUuid: string;
      payload: RegularizationActionPayload;
    }) => actionRegularizationRequest(requestUuid, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['REGULARIZATION_REQUEST', variables.requestUuid] });
      queryClient.invalidateQueries({ queryKey: ['REGULARIZATION_REQUESTS'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ATTENDANCE_TODAY_STATUS });
      const actionText = variables.payload.action === 'APPROVED' ? 'approved' : 'rejected';
      toast.success(`Regularization request ${actionText} successfully!`);
    },
    onError: (error: ApiError, variables) => {
      queryClient.invalidateQueries({ queryKey: ['REGULARIZATION_REQUEST', variables.requestUuid] });
      queryClient.invalidateQueries({ queryKey: ['REGULARIZATION_REQUESTS'] });
      if (error?.status === 409) {
        toast.error("This request is no longer pending and has already been processed.");
      } else {
        toast.error(error.message || "Failed to take action on regularization request");
      }
    },
  });
};
